/* Каркас: навигация по ролям, верхняя панель с уведомлениями, нижняя навигация на телефоне. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ROLES, ago, type Role } from "@okno/shared";
import { api } from "./lib/api";
import { useActions } from "./lib/actions";
import { TitleCtx, useScreenTitle, type TitleState } from "./lib/screenTitle";
import { locKey, useSession } from "./lib/session";
import type { Notice } from "./lib/types";
import { Icon } from "./ui/Icon";
import { Empty, MenuButton, type MenuItem } from "./ui/kit";
import { Drawer, useLayers } from "./ui/layers";

export interface NavItem {
  id: string;
  label: string;
  short?: string;
  icon: string;
  roles: Role[];
  group?: string;
}

export const NAV: NavItem[] = [
  { id: "shift", label: "Смена", icon: "gauge", roles: ["owner", "manager", "admin"] },
  { id: "tasks", label: "Мои работы", icon: "wrench", roles: ["worker"] },
  { id: "calendar", label: "Календарь", icon: "calendar", roles: ["owner", "manager", "admin", "worker"] },
  { id: "orders", label: "Заказы", icon: "clipboard", roles: ["owner", "manager", "admin"] },
  { id: "clients", label: "Клиенты", icon: "users", roles: ["owner", "manager", "admin"] },
  { id: "offers", label: "Смарт-предложения", short: "Предложения", icon: "zap", roles: ["owner", "manager"], group: "Выручка" },
  { id: "analytics", label: "Аналитика", icon: "chart", roles: ["owner", "manager"], group: "Выручка" },
  { id: "settings", label: "Настройки", icon: "settings", roles: ["owner", "manager"], group: "Управление" },
  { id: "audit", label: "Журнал действий", short: "Журнал", icon: "history", roles: ["owner", "manager"], group: "Управление" },
];

export function allowedNav(role: Role): NavItem[] {
  return NAV.filter((n) => n.roles.includes(role));
}

/* ---------- тема ---------- */

function toggleTheme(): void {
  const cur = document.documentElement.getAttribute("data-theme");
  const dark = cur ? cur === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
  const next = dark ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", next);
  try {
    localStorage.setItem("okno-theme", next);
  } catch {
    /* тема не запомнится — не критично */
  }
}

/* ---------- уведомления ---------- */

const NOTICE_ICON: Record<string, string> = { booking: "calendar", cancel: "ban", error: "alert", payment: "card", info: "info", offer: "zap" };

function NotificationsDrawer({ list }: { list: Notice[] }) {
  return (
    <Drawer title="Уведомления" sub="Новые записи, отмены, оплаты и сбои интеграций">
      {list.length ? (
        <div className="list" style={{ margin: -16 }}>
          {list.map((n) => (
            <div key={n.id} className="list-item" style={n.read ? undefined : { background: "var(--panel-2)" }}>
              <span
                className="avatar"
                style={
                  n.kind === "error"
                    ? { background: "var(--st-bad-soft)", color: "var(--st-bad)" }
                    : n.kind === "offer"
                      ? { background: "var(--signal-soft)", color: "var(--ink)" }
                      : undefined
                }
              >
                <Icon name={NOTICE_ICON[n.kind] || "info"} size={16} />
              </span>
              <div className="grow">
                <div>{n.text}</div>
                <div className="cell-sub">{ago(n.at)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty icon="bell" title="Уведомлений нет" text="Здесь появятся новые записи, отмены и оплаты." />
      )}
    </Drawer>
  );
}

/* ---------- каркас ---------- */

export function Shell() {
  const s = useSession();
  const nav = allowedNav(s.role);
  const loc = useLocation();
  const navigate = useNavigate();
  const actions = useActions();
  const { replace, depth } = useLayers();
  const qc = useQueryClient();
  const [title, setTitle] = useState<TitleState>({ title: "", sub: "" });
  const canBook = s.can("booking");
  const current = loc.pathname.split("/")[1] || "";

  const notices = useQuery({
    queryKey: locKey(s.locationId, "notifications"),
    queryFn: () => api.get<Notice[]>("/locations/" + s.locationId + "/notifications"),
    refetchInterval: 60000,
  });
  const unread = (notices.data || []).filter((n) => !n.read).length;

  const openNotices = useCallback(async () => {
    replace(<NotificationsDrawer list={notices.data || []} />);
    if (unread) {
      await api.post("/locations/" + s.locationId + "/notifications/read").catch(() => undefined);
      void qc.invalidateQueries({ queryKey: locKey(s.locationId, "notifications") });
    }
  }, [replace, notices.data, unread, s.locationId, qc]);

  // N — новая запись (как в макете), если не открыт слой и фокус не в поле ввода
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (depth || e.ctrlKey || e.metaKey || e.altKey || !canBook) return;
      if (/INPUT|SELECT|TEXTAREA/.test((document.activeElement as HTMLElement)?.tagName || "")) return;
      if (e.key === "n" || e.key === "т") {
        e.preventDefault();
        actions.openBooking();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [depth, canBook, actions]);

  const otherLocations = s.me.locations.filter((l) => l.id !== s.locationId);
  const moreItems: MenuItem[] = [
    ...nav.slice(4).map((n) => ({ label: n.label, icon: n.icon, fn: () => navigate("/" + n.id) })),
    ...otherLocations.map((l) => ({ label: "Точка: " + l.name, icon: "layers", fn: () => s.setLocation(l.id) })),
    { label: "Тёмная / светлая тема", icon: "contrast", fn: toggleTheme },
    { label: "Выйти", icon: "user", fn: () => void s.logout() },
  ];

  let lastGroup: string | undefined;
  return (
    <TitleCtx.Provider value={setTitle}>
      <div className="app">
        <aside className="sidebar">
          <NavLink className="brand" to="/">
            <span className="brand-mark">
              <span />
            </span>
            <span>
              <span className="brand-name">Окно</span>
              <span className="brand-sub">CRM для моек и СТО</span>
            </span>
          </NavLink>
          {s.me.locations.length > 1 ? (
            <div className="location-switch">
              <label htmlFor="loc-select">Точка</label>
              <select id="loc-select" className="select" value={s.locationId} onChange={(e) => s.setLocation(e.target.value)}>
                {s.me.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {(l.vertical === "sto" ? "СТО" : "Автомойка") + " · " + l.address.replace(l.city + ", ", "")}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <nav className="nav" aria-label="Разделы">
            {nav.map((n) => {
              const head = n.group && n.group !== lastGroup ? n.group : null;
              lastGroup = n.group;
              return (
                <div key={n.id} style={{ display: "contents" }}>
                  {head ? <div className="nav-group-title">{head}</div> : null}
                  <NavLink className="nav-link" to={"/" + n.id}>
                    <Icon name={n.icon} size={19} />
                    <span className="label">{n.label}</span>
                  </NavLink>
                </div>
              );
            })}
          </nav>
          <div className="sidebar-foot">
            <div className="foot-text" style={{ fontSize: 13 }}>
              <div className="cell-main">{s.me.name}</div>
              <div className="cell-sub">{ROLES[s.role]}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => void s.logout()} title="Выйти">
              <Icon name="user" size={16} />
              <span className="foot-text">Выйти</span>
            </button>
          </div>
        </aside>
        <div className="main">
          <header className="topbar">
            <div className="topbar-title">
              <h1>{title.title}</h1>
              <span className="sub">{title.sub}</span>
            </div>
            <div className="topbar-actions">
              {canBook ? (
                <button className="btn btn-primary" onClick={() => actions.openBooking()} title="Новая запись (N)">
                  <Icon name="plus" />
                  <span className="hide-mobile">Новая запись</span>
                </button>
              ) : null}
              <button className="btn btn-icon" onClick={() => void openNotices()} aria-label={"Уведомления" + (unread ? ", новых: " + unread : "")} style={{ position: "relative" }}>
                <Icon name="bell" />
                {unread ? (
                  <span className="badge" style={{ position: "absolute", top: -6, right: -6 }}>
                    {unread}
                  </span>
                ) : null}
              </button>
              <button className="btn btn-icon hide-mobile" onClick={toggleTheme} aria-label="Переключить тему">
                <Icon name="contrast" />
              </button>
              <MenuButton className="btn btn-icon mobile-more" ariaLabel="Ещё" items={moreItems}>
                <Icon name="more" />
              </MenuButton>
            </div>
          </header>
          <main className="content" id="main" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
      <nav className="bottom-nav" aria-label="Разделы">
        {nav.slice(0, 4).map((n) => (
          <NavLink key={n.id} to={"/" + n.id}>
            <Icon name={n.icon} size={20} />
            <span>{n.short || n.label}</span>
          </NavLink>
        ))}
        {nav.length > 4 ? (
          <MenuButton className={"bottom-nav-more" + (nav.slice(4).some((n) => n.id === current) ? " is-current" : "")} ariaLabel="Ещё" items={moreItems}>
            <Icon name="menu" size={20} />
            <span>Ещё</span>
          </MenuButton>
        ) : null}
      </nav>
    </TitleCtx.Provider>
  );
}

/** Для экранов-заглушек и проверки маршрутов. */
export function ScreenPlaceholder({ title }: { title: string }): ReactNode {
  useScreenTitle(title);
  return <Empty icon="info" title={title} text="Экран в разработке" />;
}
